// The logic of the policy editor and list, with no React: what a policy is called, how it is summed up, how a stored document becomes
// form state and back, and which field an API complaint is about. The kinds and their fields come from GET v3/policykinds, so nothing
// here knows a kind by name: the API describes them, and this only lays them out.
import { ruleText, switchesOf } from './ruleSwitches';

const list = (v) => (Array.isArray(v) ? v : []);
const text = (v) => (v === null || v === undefined ? '' : String(v));
const present = (v) => v !== null && v !== undefined && String(v).trim() !== '';

export const filterText = (policy) => list(policy && policy.filter).map((f) => `${f.label} is ${f.value}`).join(', ');

// What a policy applies to, for the list: its filter, its key, or the whole account.
export const subjectOf = (doc) => {
  if (doc.kind === 'cveexclusion') return doc.subject ? `${doc.cve} (${doc.subject})` : text(doc.cve);
  if (doc.kind === 'definition') return doc.displayName && doc.displayName !== doc.key ? `${doc.key} (${doc.displayName})` : text(doc.key);
  if (list(doc.filter).length) return filterText(doc);
  return doc.account === '*' ? 'Every customer' : 'The whole account';
};

const pluralise = (n, word) => `${n} ${word}`;

// A few words about what is in a policy, for the list. `spec` is the kind's description from GET v3/policykinds.
export const summaryOf = (doc, spec) => {
  if (doc.kind === 'softwarepolicy') {
    const counts = {};
    list(doc.rules).forEach((r) => { const what = ruleText(r); counts[what] = (counts[what] || 0) + 1; });
    const parts = Object.keys(counts).map((what) => pluralise(counts[what], what));
    return parts.length ? parts : ['no rules'];
  }
  if (doc.kind === 'unlistedsoftware') return [`unlisted software: ${text(doc.unlistedSoftware)}`];
  if (doc.kind === 'definition') return Object.keys(doc.actions || {}).length ? Object.keys(doc.actions).map((a) => `${a} overridden`) : ['standard actions'];
  if (doc.kind === 'actiondefaults') return Object.keys(doc.actions || {});
  if (doc.kind === 'patchpolicy' || doc.kind === 'rebootpolicy') {
    const limits = ((list(spec && spec.fields).find((f) => f.type === 'limits') || {}).limits) || [];
    const labelOf = (name) => (limits.find((l) => l.name === name) || {}).label || name;
    const body = (doc.kind === 'patchpolicy' ? doc.patch : doc.reboot) || {};
    return Object.keys(body).filter((n) => n !== 'minFeatureLabel').map((n) => `${labelOf(n)}: ${body[n]}`);
  }
  if (doc.kind === 'cvepolicy') return Object.keys(doc.cve || {}).map((n) => `${n}: ${doc.cve[n]}`);
  if (doc.kind === 'cveexclusion') return [doc.subject ? `for ${doc.subject}` : 'every program'];
  return [];
};

export const todayText = (now = new Date()) => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

// 'none', 'active' (still loosening a limit, the end date itself counts) or 'ended' (ignored by the API from the next day).
export const exceptionState = (exception, today = todayText()) => {
  if (!exception) return { state: 'none', text: '' };
  const ended = !/^\d{4}-\d{2}-\d{2}$/.test(text(exception.expires)) || exception.expires < today;
  return { state: ended ? 'ended' : 'active', text: `${ended ? 'ended' : 'until'} ${exception.expires}` };
};

// ---- the form ----------------------------------------------------------------------------------------------------------

const emptyException = { on: false, reason: '', approvedBy: '', expires: '' };

const blank = (field) => {
  switch (field.type) {
    case 'filter': return [{ label: '', value: '' }];
    case 'flag': return field.default === undefined ? false : field.default;
    case 'enum': return field.default !== undefined ? field.default : (list(field.options)[0] || '');
    case 'limits': return {};
    case 'rules': return [];
    case 'exception': return { ...emptyException, on: !!field.required };
    default: return '';                                    // text and json
  }
};

const prettyJson = (value) => (value === undefined || value === null ? '' : JSON.stringify(value, null, 2));

export const emptyForm = (spec) => Object.fromEntries(list(spec && spec.fields).map((f) => [f.name, blank(f)]));

// Form state from a stored policy.
export const formOf = (spec, doc) => {
  const form = emptyForm(spec);
  list(spec && spec.fields).forEach((f) => {
    const value = doc[f.name];
    if (value === undefined || value === null) return;
    if (f.type === 'filter') form[f.name] = list(value).map((p) => ({ label: text(p.label), value: text(p.value) }));
    else if (f.type === 'limits') form[f.name] = Object.fromEntries(Object.keys(value).map((k) => [k, text(value[k])]));
    else if (f.type === 'rules') form[f.name] = list(value).map((r) => ({ key: text(r.key), ...switchesOf(r), enabled: r.enabled !== false }));
    else if (f.type === 'json') form[f.name] = prettyJson(value);
    else if (f.type === 'exception') form[f.name] = { on: true, reason: text(value.reason), approvedBy: text(value.approvedBy), expires: text(value.expires) };
    else form[f.name] = value;
  });
  return form;
};

// The document to send, from the form: { doc } or { error, field }. Only what is needed to build the request is checked here (required fields,
// whole numbers, JSON that parses); everything else is the API's to judge, and its message is shown as it is.
export const bodyOf = (spec, form, account) => {
  const doc = { kind: spec.kind, account };
  for (const f of list(spec.fields)) {
    const value = form[f.name];
    if (f.type === 'filter') {
      const pairs = list(value).filter((p) => present(p.label) && present(p.value)).map((p) => ({ label: p.label.trim(), value: p.value.trim() }));
      if (!pairs.length && f.required) return { error: 'Add at least one label and value to the filter.', field: f.name };
      if (pairs.length) doc[f.name] = pairs;
    } else if (f.type === 'text') {
      if (present(value)) doc[f.name] = value.trim();
      else if (f.required) return { error: `${f.label} is required.`, field: f.name };
    } else if (f.type === 'flag') {
      doc[f.name] = !!value;
    } else if (f.type === 'enum') {
      doc[f.name] = value;
    } else if (f.type === 'limits') {
      const out = {};
      const names = new Set([...list(f.limits).map((l) => l.name), ...list(f.extra).map((l) => l.name)]);
      const extra = new Set(list(f.extra).map((l) => l.name));
      for (const name of Object.keys(value || {})) {
        if (!present(value[name]) || !names.has(name)) continue;
        if (extra.has(name)) { out[name] = value[name].trim(); continue; }
        const n = Number(value[name]);
        if (!Number.isInteger(n)) return { error: `${(list(f.limits).find((l) => l.name === name) || {}).label || name} must be a whole number.`, field: f.name };
        out[name] = n;
      }
      if (!Object.keys(out).length && f.required) return { error: 'Set at least one limit.', field: f.name };
      if (Object.keys(out).length) doc[f.name] = out;
    } else if (f.type === 'rules') {
      doc[f.name] = list(value).filter((r) => present(r.key)).map((r) => ({ key: r.key.trim(), ...switchesOf(r), enabled: r.enabled !== false }));
    } else if (f.type === 'json') {
      if (!present(value)) {
        if (f.required) return { error: `${f.label} is required.`, field: f.name };
      } else {
        try { doc[f.name] = JSON.parse(value); } catch (ex) { return { error: `${f.label} is not valid JSON: ${ex.message}`, field: f.name }; }
      }
    } else if (f.type === 'exception') {
      if (value && (value.on || f.required)) doc[f.name] = { reason: text(value.reason).trim(), approvedBy: text(value.approvedBy).trim(), expires: text(value.expires).trim() };
    }
  }
  return { doc };
};

// Which field an API complaint is about, so it can be shown beside that field. The messages name a field ("patch.maxFailures must be...",
// "filter needs at least one label", "exception.expires must be...", "rule key is not valid"); a message that names none is shown at the top.
export const fieldOfProblem = (message, spec) => {
  const m = text(message).toLowerCase();
  const fields = list(spec && spec.fields);
  const byName = fields.find((f) => m.startsWith(`${f.name.toLowerCase()}.`) || m.startsWith(`${f.name.toLowerCase()} `));
  if (byName) return byName.name;
  const pick = (type, words) => (words.some((w) => m.includes(w)) ? (fields.find((f) => f.type === type) || {}).name || null : null);
  return pick('filter', ['filter', 'account label']) || pick('rules', ['rule', 'program']) || pick('exception', ['exception']) || null;
};

// ---- the action a program will run ---------------------------------------------------------------------------------------
// The same rule as the API's actions.py: a definition changes a default action, it does not repeat it. script and requiresApproval
// replace the default's; the splats are added to the default's parameters; whatever is not named is inherited.

export const ACTION_NAMES = ['install', 'update', 'uninstall'];

// {action: default} from the actiondefaults policies that apply (global first, so the customer's own replaces it, per action).
export const effectiveDefaults = (policies) => {
  const out = {};
  [...list(policies)].sort((a, b) => (a.scope === 'global' ? 0 : 1) - (b.scope === 'global' ? 0 : 1)).forEach((p) => {
    if (p.enabled === false) return;
    Object.keys(p.actions || {}).forEach((name) => { if (ACTION_NAMES.includes(name) && p.actions[name]) out[name] = p.actions[name]; });
  });
  return out;
};

export const mergeAction = (base, change) => {
  const d = base || {};
  const c = change || {};
  const script = c.script !== undefined ? c.script : d.script;
  if (!script) return null;
  return {
    script,
    requiresApproval: c.requiresApproval !== undefined ? c.requiresApproval : !!d.requiresApproval,
    splat: { ...(d.splat || {}), ...(c.splat || {}) },
    dryRunSplat: { ...(d.dryRunSplat || {}), ...(c.dryRunSplat || {}) },
    applySplat: { ...(d.applySplat || {}), ...(c.applySplat || {}) },
  };
};

// {action: what runs} for a program: the defaults with the definition's changes. An action with nothing to run is left out.
const WINGET_ID = '{wingetId}';

const fillText = (value, wingetId) => {
  if (typeof value === 'string' && value.includes(WINGET_ID)) return wingetId ? value.split(WINGET_ID).join(wingetId) : null;
  if (Array.isArray(value)) {
    const filled = value.map((v) => fillText(v, wingetId));
    return filled.some((f, i) => f === null && typeof value[i] === 'string' && value[i].includes(WINGET_ID)) ? null : filled;
  }
  return value;
};

// The action with {wingetId} filled in, or null when it needs one and the program has none (the same as the API's actions.fill_winget_id).
export const fillWingetId = (action, wingetId) => {
  const out = { ...action };
  for (const field of ['splat', 'dryRunSplat', 'applySplat']) {
    const filled = {};
    for (const key of Object.keys(action[field] || {})) {
      const replaced = fillText(action[field][key], wingetId);
      if (replaced === null && action[field][key] !== null) return null;
      filled[key] = replaced;
    }
    out[field] = filled;
  }
  return out;
};

// wingetId: a string fills {wingetId}; null or '' means the program is known to have none, so the actions that need one are left out; undefined
// means it is not known yet (the definition is still being typed), so the placeholder is shown as it is.
export const resolveActions = (defaultPolicies, changes, wingetId) => {
  const defaults = effectiveDefaults(defaultPolicies);
  const out = {};
  ACTION_NAMES.forEach((name) => {
    let merged = mergeAction(defaults[name], (changes || {})[name]);
    if (merged && wingetId !== undefined) merged = fillWingetId(merged, wingetId);
    if (merged) out[name] = merged;
  });
  return out;
};

// "uninstall: Uninstall-Software.ps1 -Name {installedName} -SilentArguments /silent (needs approval)"
export const describeAction = (name, action) => {
  const leaf = text(action.script).split('/').pop();
  const params = Object.keys(action.splat || {}).map((k) => ` -${k} ${Array.isArray(action.splat[k]) ? action.splat[k].join(',') : action.splat[k]}`).join('');
  return `${name}: ${leaf}${params}${action.requiresApproval ? '  (needs approval)' : ''}`;
};

// Has the identifying filter of an existing policy been changed? Saving then makes a new policy and leaves the original.
export const filterChanged = (original, form) => {
  const norm = (pairs) => list(pairs).filter((p) => present(p.label) && present(p.value)).map((p) => `${p.label.trim().toLowerCase()}=${p.value.trim().toLowerCase()}`).sort().join(';');
  return norm(original && original.filter) !== norm(form && form.filter);
};
